"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, type HTMLMotionProps, motion } from "framer-motion";
import {
	type ComponentPropsWithoutRef,
	type ElementRef,
	type HTMLAttributes,
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useState,
} from "react";

import { CloseIcon } from "@moment/icons";

import { IconButton } from "../Button";
import { cn } from "../utils";

/**
 * Tracks the open state for DialogContent's AnimatePresence.
 * In controlled mode this mirrors the caller's `open` prop; in uncontrolled
 * mode it is kept in sync via the `onOpenChange` callback from Radix.
 */
const DialogOpenContext = createContext<boolean>(false);

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const overlayMotionProps = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	exit: { opacity: 0 },
	transition: { duration: 0.15, ease: "easeOut" },
} satisfies HTMLMotionProps<"div">;

const contentMotionProps = {
	initial: { opacity: 0, scale: 0.95 },
	animate: { opacity: 1, scale: 1 },
	exit: { opacity: 0, scale: 0.95 },
	transition: { type: "spring", duration: 0.3, bounce: 0.1 },
} satisfies HTMLMotionProps<"div">;

/**
 * Wrapper around Radix Dialog.Root that tracks the open state via context
 * so DialogContent can drive AnimatePresence.
 *
 * Supports both controlled (`open` + `onOpenChange`) and uncontrolled
 * (`defaultOpen` / `DialogTrigger`) usage. In uncontrolled mode the
 * internal state is kept in sync via Radix's `onOpenChange` callback.
 */
const Dialog = ({
	open: controlledOpen,
	defaultOpen,
	onOpenChange,
	children,
	...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => {
	const isControlled = controlledOpen !== undefined;
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);

	const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (!isControlled) {
				setUncontrolledOpen(nextOpen);
			}
			onOpenChange?.(nextOpen);
		},
		[isControlled, onOpenChange]
	);

	return (
		<DialogOpenContext.Provider value={isOpen}>
			<DialogPrimitive.Root
				open={controlledOpen}
				defaultOpen={defaultOpen}
				onOpenChange={handleOpenChange}
				{...props}
			>
				{children}
			</DialogPrimitive.Root>
		</DialogOpenContext.Provider>
	);
};
Dialog.displayName = "Dialog";

const DialogOverlay = forwardRef<
	ElementRef<typeof DialogPrimitive.Overlay>,
	ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn("fixed inset-0 z-dialog bg-modal", className)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Animated dialog content following the Radix animation guide:
 * https://www.radix-ui.com/primitives/docs/guides/animation
 *
 * Uses `forceMount` + `asChild` on both Overlay and Content so that
 * framer-motion's AnimatePresence controls the mount lifecycle and can
 * play exit animations before elements are removed from the DOM.
 *
 * The open state is read from context provided by `<Dialog>`, so
 * callers don't need to pass it again. Works with both controlled and
 * uncontrolled Dialog usage.
 */
const DialogContent = forwardRef<
	ElementRef<typeof DialogPrimitive.Content>,
	ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
		width?: "sm" | "md" | "lg" | "xl" | "2xl";
		hideCloseButton?: boolean;
		/** When true, the dialog is anchored from its top edge instead of vertically centered. */
		anchorTop?: boolean;
	}
>(
	(
		{ className, children, width = "sm", hideCloseButton = false, anchorTop = false, ...props },
		ref
	) => {
		const open = useContext(DialogOpenContext);

		return (
			<AnimatePresence>
				{open ? (
					<DialogPortal forceMount>
						<DialogPrimitive.Overlay forceMount asChild>
							<motion.div
								className="fixed inset-0 z-dialog bg-modal"
								{...overlayMotionProps}
							/>
						</DialogPrimitive.Overlay>

						<DialogPrimitive.Content ref={ref} forceMount asChild {...props}>
							<motion.div
								className={cn(
									"fixed",
									"text-sm",
									"left-[50%]",
									!anchorTop && "top-[50%]",
									"z-dialog",
									"grid",
									"w-full",
									"gap-4",
									"bg-primary",
									"p-6",
									"shadow-lg",
									"sm:rounded-lg",
									"overflow-hidden",
									{
										"max-w-lg": width === "sm",
										"max-w-xl": width === "md",
										"max-w-2xl": width === "lg",
										"max-w-3xl": width === "xl",
										"max-w-4xl": width === "2xl",
									},
									className
								)}
								{...contentMotionProps}
								style={{ x: "-50%", y: anchorTop ? 0 : "-50%" }}
							>
								{children}
								{!hideCloseButton && (
									<DialogPrimitive.Close asChild>
										<IconButton
											preset="quaternary"
											icon={<CloseIcon />}
											label="Close"
											size="sm"
											className={cn("!absolute", "right-4", "top-4")}
										/>
									</DialogPrimitive.Close>
								)}
							</motion.div>
						</DialogPrimitive.Content>
					</DialogPortal>
				) : null}
			</AnimatePresence>
		);
	}
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
		{...props}
	/>
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
		{...props}
	/>
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = forwardRef<
	ElementRef<typeof DialogPrimitive.Title>,
	ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn("text-lg font-semibold leading-none tracking-tight", className)}
		{...props}
	/>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
	ElementRef<typeof DialogPrimitive.Description>,
	ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn("text-sm text-muted-foreground", className)}
		{...props}
	/>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
